import React, { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import TopBar from '../components/TopBar';
import { useNavigate } from 'react-router-dom';
import layoutStyles from '../styles/layout.module.css';
import styles from './Account.module.css';
import { auth } from '../firebase';

function handleBack(navigate) {
  if (window.history.length > 2) navigate(-1);
  else navigate('/account');
}

function mapAuthError(error) {
  const code = String(error?.code || '').trim();
  if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') return 'Your current password is incorrect.';
  if (code === 'auth/weak-password') return 'Choose a stronger password with at least 6 characters.';
  if (code === 'auth/requires-recent-login') return 'Please sign in again, then retry this change.';
  return error?.message || 'Unable to update your password.';
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    const currentUser = auth.currentUser;
    const hasPasswordProvider = currentUser?.providerData?.some((provider) => provider.providerId === 'password');

    if (!currentUser || !currentUser.email) {
      setError('You must be signed in with an email/password account to change your password.');
      return;
    }
    if (!hasPasswordProvider) {
      setError('This account does not use password sign-in, so password changes are not available here.');
      return;
    }
    if (!currentPassword) {
      setError('Enter your current password.');
      return;
    }
    if (nextPassword.length < 6) {
      setError('Choose a new password with at least 6 characters.');
      return;
    }
    if (nextPassword !== confirmPassword) {
      setError('Your new passwords do not match.');
      return;
    }
    if (nextPassword === currentPassword) {
      setError('Choose a password that is different from your current one.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const credential = EmailAuthProvider.credential(currentUser.email, currentPassword);
      await reauthenticateWithCredential(currentUser, credential);
      await updatePassword(currentUser, nextPassword);
      setSuccess('Your password has been updated.');
      setCurrentPassword('');
      setNextPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => handleBack(navigate)} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <div className={styles.pageInner}>
          <section className={styles.heroCard}>
            <span className={styles.eyebrow}>Account</span>
            <h1 className={styles.title}>Change Password</h1>
            <p className={styles.subtitle}>Re-authenticate with your current password, then choose a stronger one for future sign-ins.</p>
          </section>

          <section className={styles.sectionCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Security</span>
                <h2 className={styles.sectionTitle}>Update your sign-in password</h2>
              </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.field}>
                <span>Current Password</span>
                <input value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} placeholder="Current password" type="password" autoComplete="current-password" />
              </label>
              <label className={styles.field}>
                <span>New Password</span>
                <input value={nextPassword} onChange={(event) => setNextPassword(event.target.value)} placeholder="At least 6 characters" type="password" autoComplete="new-password" />
              </label>
              <label className={styles.field}>
                <span>Confirm New Password</span>
                <input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Re-enter new password" type="password" autoComplete="new-password" />
              </label>
              <p className={styles.helperText}>Use at least 6 characters and avoid reusing the same password you already have.</p>
              {error ? <p className={styles.errorState}>{error}</p> : null}
              {success ? <p className={styles.successState}>{success}</p> : null}
              <div className={styles.actions}>
                <button type="button" className={styles.secondaryButton} onClick={() => navigate('/account')}>Back to Account</button>
                <button type="submit" className={styles.primaryButton} disabled={saving}>{saving ? 'Saving…' : 'Save Password'}</button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
